import lodashExtend from "lodash/extend";
import type React from "react";

import { HealthStateValue, type ViewComponent } from "~/models/dto";

import { Api } from "./openapi";

// merge via intersection types
export function extend<T, U>(first: T, second: U): T & U {
	return lodashExtend({}, second, first); // definitions in first object must override the same keys in second
}

export function stateToText(state: Api.HealthStateValue | HealthStateValue): string {
	// noinspection TypeScriptValidateTypes
	return String(state).toLowerCase().replaceAll("_", " ");
}

export const healthStateToColorToken = (
	healthState: undefined | Api.HealthStateValue | HealthStateValue,
): `--${string}` => {
	switch (healthState) {
		case Api.HealthStateValue.CRITICAL:
		case HealthStateValue.Critical:
			return "--dataviz-fill-health-timeline-critical";
		case Api.HealthStateValue.FLAPPING:
		case HealthStateValue.Flapping:
		case Api.HealthStateValue.DEVIATING:
		case HealthStateValue.Deviating:
			return "--dataviz-fill-health-timeline-deviating";
		case Api.HealthStateValue.CLEAR:
		case HealthStateValue.Clear:
			return "--dataviz-fill-health-timeline-clear";
		case Api.HealthStateValue.UNKNOWN:
		case HealthStateValue.Unknown:
			return "--dataviz-fill-health-timeline-unknown";
		default:
			return "--dataviz-fill-health-timeline-none";
	}
};

export function healthStateToTextColorToken(healthState: Api.HealthStateValue | HealthStateValue): `--${string}` {
	switch (healthState) {
		case HealthStateValue.Critical:
		case Api.HealthStateValue.CRITICAL:
			return "--core-text-alert-primary";
		case HealthStateValue.Flapping:
		case Api.HealthStateValue.FLAPPING:
		case HealthStateValue.Deviating:
		case Api.HealthStateValue.DEVIATING:
			return "--core-text-warning-primary";
		case HealthStateValue.Clear:
		case Api.HealthStateValue.CLEAR:
			return "--core-text-success-primary";
		default:
			return "--core-text-unavailable-primary";
	}
}

export const healthStateToBackgroundColorToken = (
	healthState: HealthStateValue | Api.HealthStateValue,
): `--${string}` => {
	switch (healthState) {
		case HealthStateValue.Critical:
		case Api.HealthStateValue.CRITICAL:
			return "--dataviz-fill-visualizer-components-critical";
		case HealthStateValue.Flapping:
		case Api.HealthStateValue.FLAPPING:
		case HealthStateValue.Deviating:
		case Api.HealthStateValue.DEVIATING:
			return "--dataviz-fill-visualizer-components-deviating";
		case HealthStateValue.Clear:
		case Api.HealthStateValue.CLEAR:
			return "--dataviz-fill-visualizer-components-clear";
		default:
			return "--dataviz-fill-visualizer-components-unknown";
	}
};

export const healthStateToRelationDartboardColorToken = (
	healthState: HealthStateValue | Api.HealthStateValue,
): `--${string}` => {
	switch (healthState) {
		case HealthStateValue.Critical:
		case Api.HealthStateValue.CRITICAL:
			return "--dataviz-fill-visualizer-components-critical";
		case HealthStateValue.Flapping:
		case Api.HealthStateValue.FLAPPING:
		case HealthStateValue.Deviating:
		case Api.HealthStateValue.DEVIATING:
			return "--dataviz-fill-visualizer-components-deviating";
		case HealthStateValue.Clear:
		case Api.HealthStateValue.CLEAR:
			return "--dataviz-fill-visualizer-components-clear";
		default:
			return "--dataviz-fill-visualizer-ixd-states-relation-disc";
	}
};

/**
 * Check if health state is unhealthy
 * Atm all states except UNKNOWN and CLEAR are considered as unhealthy
 * @param healthState
 */
export const isHealthStateUnHealthy = (healthState: Api.HealthStateValue | HealthStateValue): boolean => {
	switch (healthState) {
		case HealthStateValue.Unknown:
		case Api.HealthStateValue.UNKNOWN:
		case HealthStateValue.Clear:
		case Api.HealthStateValue.CLEAR:
		case HealthStateValue.Disabled:
		case Api.HealthStateValue.DISABLED:
			return false;
		default:
			return true;
	}
};

export const getNumberOfFailingChecks = (component: ViewComponent): number =>
	component.failingChecks
		? component.failingChecks.filter((failingChecks) => isHealthStateUnHealthy(failingChecks.state.state)).length
		: 0;
